import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate } from 'react-router-dom';
import { signupSchema } from '@app/shared';
import type { SignupInput, SignupResultDto } from '@app/shared';
import { useBrandLook, useShellColours } from '../../app/BrandTheme';
import { APP_NAME } from '../../app/theme';
import { AppLockup } from '../../components/BrandLogo';
import { TextInput, applyServerErrors } from '../../components/form';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from './AuthProvider';

/**
 * Public: an MVNO signs itself up (route /signup, behind the release switch `mvno-signup`). It
 * gets its own company in Trial and its first administrator, then signs in and sets up.
 */
export function SignupPage() {
  const { status } = useAuth();
  const colours = useShellColours();
  const brandLook = useBrandLook();
  const [done, setDone] = useState<SignupResultDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    setError: setFieldError,
    formState: { isSubmitting },
  } = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      companyName: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      loginId: '',
      password: '',
      carriers: '',
    },
  });
  if (status === 'signedIn') return <Navigate to="/" replace />;

  const submit = async (input: SignupInput) => {
    setError(null);
    try {
      setDone(await api.post<SignupResultDto>('/signup', input));
    } catch (err) {
      if (!applyServerErrors(err, setFieldError)) setError(errorMessage(err));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        background: colours.loginBg,
      }}
    >
      <Paper elevation={4} sx={{ width: '100%', maxWidth: 560, p: { xs: 2.5, sm: 4 } }}>
        <Typography variant="h1" sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          {brandLook ? <AppLockup height={72} /> : APP_NAME}
        </Typography>
        {done ? (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Alert severity="success">
              {done.companyName} is set up. Sign in as <b>{done.loginId}</b> with the password you
              chose.
            </Alert>
            <Typography variant="body2">
              Your company starts in <b>Trial</b>: services you have not connected yet are
              simulated, so you can try everything. After signing in, open Settings &gt; Get
              started: it lists every step (carriers, plans, service area, payments, staff) and,
              when they are done, lets you ask {APP_NAME} to move you to Live.
            </Typography>
            <Button component={Link} to="/login" variant="contained" size="large">
              Sign in
            </Button>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={(e) => void handleSubmit(submit)(e)}
            noValidate
            sx={{ display: 'grid', gap: 2 }}
          >
            <Typography variant="h2" align="center">
              Start your MVNO on {APP_NAME}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Your own company with its own users, carriers, plans and customers. You become its
              administrator.
            </Typography>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextInput control={control} name="companyName" label="Company name" required />
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { sm: '1fr 1fr' } }}>
              <TextInput control={control} name="firstName" label="First Name" required />
              <TextInput control={control} name="lastName" label="Last Name" required />
              <TextInput
                control={control}
                name="email"
                label="Email Address"
                type="email"
                autoComplete="email"
                required
              />
              <TextInput
                control={control}
                name="phone"
                label="Phone Number"
                autoComplete="tel"
                helperText="10 digits"
                required
              />
              <TextInput
                control={control}
                name="loginId"
                label="Login ID"
                autoComplete="username"
                required
              />
              <TextInput
                control={control}
                name="password"
                label="Password"
                type="password"
                autoComplete="new-password"
                helperText="At least 8 characters with a letter and a digit"
                required
              />
            </Box>
            <TextInput
              control={control}
              name="carriers"
              label="Carriers or MVNEs you work with"
              helperText="For example PWG, Helix. You connect them after signing in."
            />
            <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
              Create my company
            </Button>
            <Typography variant="body2" align="center">
              Already signed up? <Link to="/login">Sign in</Link>
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
